import React from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import styled from 'styled-components';
import { useChartTheme, tooltipStyle } from './chartSetup';

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
`;

const ChartCard = styled.div`
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--separator);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: clamp(18px, 2.6vw, 28px);
`;

const ChartScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  /* allow horizontal scrolling on mobile Chrome; ensure canvas doesn't block gestures */
  canvas {
    touch-action: pan-x;
    -webkit-user-select: none;
    user-select: none;
  }
  @media (max-width: 768px) {
    /* Ensure horizontal scrolling works on mobile and tablets */
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  @media (min-width: 769px) {
    /* On desktop prefer no horizontal scroll — allow charts to adapt */
    overflow-x: visible;
  }
`;

const InnerChartBox = styled.div`
  /* min-width will be set dynamically based on number of labels */
  min-width: 500px;
  box-sizing: border-box;
  padding-bottom: 6px;
  height: 360px;
  display: inline-block; /* inline-block allows overflow-x scrolling to work reliably on Chrome mobile */
  vertical-align: top;
  @media (max-width: 480px) {
    /* On small screens limit chart height to 60% of viewport height */
    height: 60dvh;
    /* Ensure horizontal scrolling works on mobile */
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  @media (min-width: 481px) and (max-width: 768px) {
    /* On tablets enable horizontal scrolling */
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  @media (min-width: 769px) {
    /* Desktop/tablet: force charts to fill available width and avoid causing horizontal scroll */
    min-width: 100% !important;
    display: block;
    height: 360px;
  }
`;

const ChartTitle = styled.h3`
  margin-bottom: 16px;
  font-size: 1.0625rem;
  font-weight: 600;
  letter-spacing: -0.014em;
  color: var(--text);
`;

const defaultChartsLocale = {
  periods: ['1 day', '30 days', '90 days', '365 days'],
  contractTypes: 'Contract type distribution',
  average: 'Average funding rates by period',
  max: 'Maximum funding rates',
  min: 'Minimum funding rates',
  total: 'Funding rate sums',
  axisRate: 'Funding rate (%)',
  axisMax: 'Maximum funding rate (%)',
  axisMin: 'Minimum funding rate (%)',
  axisTotal: 'Funding rate sum',
  axisExchanges: 'Exchanges',
  records: 'Records',
};

const FundingCharts = ({ data, locale = defaultChartsLocale }) => {
  const theme = useChartTheme();
  const t = { ...defaultChartsLocale, ...locale };
  if (!data || !data.data) return null;


  // Підготовка даних для кругової діаграми (розподіл типів контрактів)
  const prepareContractTypesData = () => {
    // Підраховуємо кількість бірж з різними типами контрактів
    const usdtContracts = data.data.filter(item =>
      item.contract_type.toUpperCase() === 'USDT-M'
    ).length;

    const coinContracts = data.data.filter(item =>
      item.contract_type.toUpperCase() === 'COIN-M'
    ).length;

    return {
      labels: [`USDT-M (${usdtContracts})`, `COIN-M (${coinContracts})`],
      datasets: [
        {
          data: [usdtContracts, coinContracts],
          backgroundColor: theme.categories,
          borderColor: theme.surface,
          borderWidth: 2,
        },
      ],
    };
  };

  // Підготовка даних для діаграми порівняння періодів
  const preparePeriodComparisonData = () => {
    const allExchanges = data.data;

    const labels = allExchanges.map(item =>
      `${item.exchange_name} (${item.contract_type.toUpperCase()})`
    );

    return {
      labels,
      datasets: [
        {
          label: `${t.periods[0]} (%)`,
          data: allExchanges.map(item => (item.period_1d?.average_funding_rate || 0) * 100),
          backgroundColor: theme.periods[0],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[1]} (%)`,
          data: allExchanges.map(item => (item.period_30d?.average_funding_rate || 0) * 100),
          backgroundColor: theme.periods[1],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[2]} (%)`,
          data: allExchanges.map(item => (item.period_90d?.average_funding_rate || 0) * 100),
          backgroundColor: theme.periods[2],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[3]} (%)`,
          data: allExchanges.map(item => (item.period_365d?.average_funding_rate || 0) * 100),
          backgroundColor: theme.periods[3],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
      ],
    };
  };

  // Підготовка даних для діаграми максимальних ставок
  const prepareMaxRatesData = () => {
    const allExchanges = data.data;

    const labels = allExchanges.map(item =>
      `${item.exchange_name} (${item.contract_type.toUpperCase()})`
    );

    return {
      labels,
      datasets: [
        {
          label: `${t.periods[0]} (%)`,
          data: allExchanges.map(item => (item.period_1d?.max_rate || 0) * 100),
          backgroundColor: theme.periods[0],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[1]} (%)`,
          data: allExchanges.map(item => (item.period_30d?.max_rate || 0) * 100),
          backgroundColor: theme.periods[1],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[2]} (%)`,
          data: allExchanges.map(item => (item.period_90d?.max_rate || 0) * 100),
          backgroundColor: theme.periods[2],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[3]} (%)`,
          data: allExchanges.map(item => (item.period_365d?.max_rate || 0) * 100),
          backgroundColor: theme.periods[3],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
      ],
    };
  };

  // Підготовка даних для діаграми мінімальних ставок
  const prepareMinRatesData = () => {
    const allExchanges = data.data;

    const labels = allExchanges.map(item =>
      `${item.exchange_name} (${item.contract_type.toUpperCase()})`
    );

    return {
      labels,
      datasets: [
        {
          label: `${t.periods[0]} (%)`,
          data: allExchanges.map(item => (item.period_1d?.min_rate || 0) * 100),
          backgroundColor: theme.periods[0],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[1]} (%)`,
          data: allExchanges.map(item => (item.period_30d?.min_rate || 0) * 100),
          backgroundColor: theme.periods[1],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[2]} (%)`,
          data: allExchanges.map(item => (item.period_90d?.min_rate || 0) * 100),
          backgroundColor: theme.periods[2],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[3]} (%)`,
          data: allExchanges.map(item => (item.period_365d?.min_rate || 0) * 100),
          backgroundColor: theme.periods[3],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
      ],
    };
  };

  // Підготовка даних для діаграми сум ставок фінансування
  const prepareTotalRatesData = () => {
    const allExchanges = data.data;

    const labels = allExchanges.map(item =>
      `${item.exchange_name} (${item.contract_type.toUpperCase()})`
    );

    return {
      labels,
      datasets: [
        {
          label: `${t.periods[0]} (%)`,
          data: allExchanges.map(item => (item.period_1d?.total_funding_rate || 0) * 100),
          backgroundColor: theme.periods[0],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[1]} (%)`,
          data: allExchanges.map(item => (item.period_30d?.total_funding_rate || 0) * 100),
          backgroundColor: theme.periods[1],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[2]} (%)`,
          data: allExchanges.map(item => (item.period_90d?.total_funding_rate || 0) * 100),
          backgroundColor: theme.periods[2],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
        {
          label: `${t.periods[3]} (%)`,
          data: allExchanges.map(item => (item.period_365d?.total_funding_rate || 0) * 100),
          backgroundColor: theme.periods[3],
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 14,
        },
      ],
    };
  };


  const contractTypesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: t.contractTypes,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${percentage}%`;
          }
        }
      },
    },
  };

  const periodComparisonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: t.average,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(8)}%`;
          },
          afterLabel: function(context) {
            const index = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            let records = 0;

            // Визначаємо період залежно від індексу датасету
            if (datasetIndex === 0) records = data.data[index].period_1d?.total_records || 0;
            else if (datasetIndex === 1) records = data.data[index].period_30d?.total_records || 0;
            else if (datasetIndex === 2) records = data.data[index].period_90d?.total_records || 0;
            else if (datasetIndex === 3) records = data.data[index].period_365d?.total_records || 0;

            return `${t.records}: ${records}`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t.axisRate
        },
        ticks: {
          callback: function(value) {
            return `${parseFloat(value.toFixed(6))}%`;
          }
        }
      },
      x: {
        title: {
          display: true,
          text: t.axisExchanges
        }
      }
    },
  };

  const maxRatesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: t.max,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const index = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            let dateInfo = '';

            if (datasetIndex === 0) {
              const date = data.data[index].period_1d?.max_date;
              dateInfo = date ? ` (${new Date(date).toLocaleString()})` : '';
            } else if (datasetIndex === 1) {
              const date = data.data[index].period_30d?.max_date;
              dateInfo = date ? ` (${new Date(date).toLocaleString()})` : '';
            } else if (datasetIndex === 2) {
              const date = data.data[index].period_90d?.max_date;
              dateInfo = date ? ` (${new Date(date).toLocaleString()})` : '';
            } else if (datasetIndex === 3) {
              const date = data.data[index].period_365d?.max_date;
              dateInfo = date ? ` (${new Date(date).toLocaleString()})` : '';
            }

            return `${context.dataset.label}: ${context.parsed.y.toFixed(8)}%${dateInfo}`;
          },
          afterLabel: function(context) {
            const index = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            let records = 0;

            if (datasetIndex === 0) records = data.data[index].period_1d?.total_records || 0;
            else if (datasetIndex === 1) records = data.data[index].period_30d?.total_records || 0;
            else if (datasetIndex === 2) records = data.data[index].period_90d?.total_records || 0;
            else if (datasetIndex === 3) records = data.data[index].period_365d?.total_records || 0;

            return `${t.records}: ${records}`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t.axisMax
        },
        ticks: {
          callback: function(value) {
            return `${parseFloat(value.toFixed(6))}%`;
          }
        }
      },
      x: {
        title: {
          display: true,
          text: t.axisExchanges
        }
      }
    },
  };

  const minRatesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: t.min,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const index = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            let dateInfo = '';

            if (datasetIndex === 0) {
              const date = data.data[index].period_1d?.min_date;
              dateInfo = date ? ` (${new Date(date).toLocaleString()})` : '';
            } else if (datasetIndex === 1) {
              const date = data.data[index].period_30d?.min_date;
              dateInfo = date ? ` (${new Date(date).toLocaleString()})` : '';
            } else if (datasetIndex === 2) {
              const date = data.data[index].period_90d?.min_date;
              dateInfo = date ? ` (${new Date(date).toLocaleString()})` : '';
            } else if (datasetIndex === 3) {
              const date = data.data[index].period_365d?.min_date;
              dateInfo = date ? ` (${new Date(date).toLocaleString()})` : '';
            }

            return `${context.dataset.label}: ${context.parsed.y.toFixed(8)}%${dateInfo}`;
          },
          afterLabel: function(context) {
            const index = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            let records = 0;

            if (datasetIndex === 0) records = data.data[index].period_1d?.total_records || 0;
            else if (datasetIndex === 1) records = data.data[index].period_30d?.total_records || 0;
            else if (datasetIndex === 2) records = data.data[index].period_90d?.total_records || 0;
            else if (datasetIndex === 3) records = data.data[index].period_365d?.total_records || 0;

            return `${t.records}: ${records}`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t.axisMin
        },
        ticks: {
          callback: function(value) {
            return `${parseFloat(value.toFixed(6))}%`;
          }
        }
      },
      x: {
        title: {
          display: true,
          text: t.axisExchanges
        }
      }
    },
  };

  const totalRatesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: t.total,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(8)}%`;
          },
          afterLabel: function(context) {
            const index = context.dataIndex;
            const datasetIndex = context.datasetIndex;
            let records = 0;

            if (datasetIndex === 0) records = data.data[index].period_1d?.total_records || 0;
            else if (datasetIndex === 1) records = data.data[index].period_30d?.total_records || 0;
            else if (datasetIndex === 2) records = data.data[index].period_90d?.total_records || 0;
            else if (datasetIndex === 3) records = data.data[index].period_365d?.total_records || 0;

            return `${t.records}: ${records}`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t.axisTotal
        },
        ticks: {
          callback: function(value) {
            return `${parseFloat(value.toFixed(6))}%`;
          }
        }
      },
      x: {
        title: {
          display: true,
          text: t.axisExchanges
        }
      }
    },
  };

  // Shared styling layered over each chart's own options. Titles are rendered as HTML
  // headings above the canvas, so Chart.js' own title stays off.
  const themed = (options) => {
    const scales = options.scales
      ? Object.fromEntries(
          Object.entries(options.scales).map(([key, scale]) => [
            key,
            {
              ...scale,
              title: scale.title && { ...scale.title, color: theme.textSecondary, font: { family: theme.font, size: 12 } },
              ticks: { ...scale.ticks, color: theme.textSecondary, font: { family: theme.font, size: 11 } },
              grid: { color: key === 'y' ? theme.grid : 'transparent', drawTicks: false },
              border: { color: theme.axis },
            },
          ])
        )
      : undefined;

    return {
      ...options,
      plugins: {
        ...options.plugins,
        title: { ...options.plugins.title, display: false },
        legend: {
          ...options.plugins.legend,
          labels: {
            color: theme.textSecondary,
            font: { family: theme.font, size: 12 },
            usePointStyle: true,
            pointStyle: 'rectRounded',
            boxWidth: 8,
            boxHeight: 8,
            padding: 16,
          },
        },
        tooltip: {
          ...options.plugins.tooltip,
          ...tooltipStyle(theme),
        },
      },
      ...(scales && { scales }),
    };
  };

  const calcMinWidth = (labelsCount) => {
    // base width for single chart, then add per-label width
    const perLabel = 110; // px per label (adjustable)
    const base = 420;
    return Math.max(base, labelsCount * perLabel);
  };

  const periodData = preparePeriodComparisonData();
  const maxData = prepareMaxRatesData();
  const minData = prepareMinRatesData();
  const totalData = prepareTotalRatesData();

  return (
    <ChartsContainer>

      <ChartCard>
        <ChartTitle>{t.contractTypes}</ChartTitle>
        <ChartScrollWrapper>
          <InnerChartBox style={{ minWidth: 300, height: 280 }}>
            <Doughnut data={prepareContractTypesData()} options={{ ...themed(contractTypesOptions), cutout: '62%' }} />
          </InnerChartBox>
        </ChartScrollWrapper>
      </ChartCard>

      <ChartCard>
        <ChartTitle>{t.average}</ChartTitle>
        <ChartScrollWrapper>
          <InnerChartBox style={{ minWidth: calcMinWidth(periodData.labels.length) }}>
            <Bar data={periodData} options={themed(periodComparisonOptions)} />
          </InnerChartBox>
        </ChartScrollWrapper>
      </ChartCard>

      <ChartCard>
        <ChartTitle>{t.max}</ChartTitle>
        <ChartScrollWrapper>
          <InnerChartBox style={{ minWidth: calcMinWidth(maxData.labels.length) }}>
            <Bar data={maxData} options={themed(maxRatesOptions)} />
          </InnerChartBox>
        </ChartScrollWrapper>
      </ChartCard>

      <ChartCard>
        <ChartTitle>{t.min}</ChartTitle>
        <ChartScrollWrapper>
          <InnerChartBox style={{ minWidth: calcMinWidth(minData.labels.length) }}>
            <Bar data={minData} options={themed(minRatesOptions)} />
          </InnerChartBox>
        </ChartScrollWrapper>
      </ChartCard>

      <ChartCard>
        <ChartTitle>{t.total}</ChartTitle>
        <ChartScrollWrapper>
          <InnerChartBox style={{ minWidth: calcMinWidth(totalData.labels.length) }}>
            <Bar data={totalData} options={themed(totalRatesOptions)} />
          </InnerChartBox>
        </ChartScrollWrapper>
      </ChartCard>
    </ChartsContainer>
  );
};

export default FundingCharts;
